import WithErrorBoundry from "@components/ErrorBoundry/WithErrorBoundry/WithErrorBoundry";
import Error, { ErrorType } from "@components/global/new/Error/Error";
import NumberOfColumn from "@components/global/new/NumberOfColumn/NumberOfColumn";
import { useWatchNetwork } from "@mobx/services/http";
import { RackStore } from "@mobx/store/locations/interfaces";
import rackTemplatesStore from "@mobx/store/locations/rackTemplate";
import { IRackSection3, IRackTemplate, IRackTemplateItem, RACK_SECTION_TYPE3, TypePartialRackItem3 } from "@mobx/store/locations/types";
import { compareMinersPositions3 } from "@mobx/utils/rackModalUtils";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { makeGrid } from "@utils/makeGrid";
import { makeNotifs } from "@utils/makeNotifs";
import { xyPositionRackItem } from "@utils/xyPositionRackItem";
import { Button } from "antd";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { RACK_UNIT } from "../../../config";
import MinerSection3 from "../MinerSection3";
import styles from "./RackEdit.module.scss";

export interface IRackEdit3Props {
  readonly type: [number, number];
  readonly lastType?: [number, number];
  readonly rackStore?: RackStore;
  readonly rackTemplate?: IRackTemplate;
  readonly rackTypeError?: string;
  readonly reversed?: boolean;

  onClose(): void;
  update?(items: TypePartialRackItem3[], type: [number, number]): void;
}

export const useControl = (
  props: IRackEdit3Props
) => {
  const isEditRack = !!props.rackTemplate || !!props.rackStore;
  const { errors, ok } = makeNotifs();
  const { onError, onSuccess } = useGlobalNotifs();
  const [minersPositions, setMinersPositions] = useState<IRackSection3[]>([]);
  const [rackSections, setRackSections] = useState<IRackSection3[]>([]);
  const [err, setErr] = useState<ErrorType>();
  const rack = props.rackStore?.rack;
  useWatchNetwork(rackTemplatesStore, props.rackStore);

  useEffect(() => {
    if (props.rackTypeError === "#") {
      setErr({ ownError: [errors.locations__edit_rack] });
    } else if (props.rackTypeError) {
      setErr({ ownError: [props.rackTypeError] });
    }
    else {
      setErr(undefined);
    }
  }, [props.rackTypeError]);

  useLayoutEffect(() => {
    const miners: IRackSection3[] = [];
    rack?.rack_items?.forEach((item) => {
      if (item.pos === RACK_SECTION_TYPE3.MINER) {
        miners.push(item);
      }
    });
    setMinersPositions(miners);
  }, [rack?.rack_items]);

  const fillRackSections = (
    arr?: (TypePartialRackItem3 | IRackTemplateItem)[]
  ): IRackSection3[] => {
    const fullLength = props.type[0] * props.type[1];
    if (!arr) {
      return Array.from(new Array(fullLength)).fill({
        pos: RACK_SECTION_TYPE3.EMPTY,
      });
    }

    let length = fullLength - arr.length;

    let items: IRackSection3[] = (arr as TypePartialRackItem3[]).map((item) => {
      return { id: item.id, pos: item.pos, x: item.x, y: item.y };
    });

    if (length < 0) {
      items = items.slice(0, items.length + length);
      length = 0;
    }

    const joinedArr: IRackSection3[] = Array.from(new Array(length)).fill({
      pos: RACK_SECTION_TYPE3.EMPTY,
    });

    let itemsWithNewXY = [...items, ...joinedArr].map((item, i) => {
      const [X, Y] = xyPositionRackItem(i, props.type);
      return { ...item, x: X, y: Y };
    });

    // возвращаем верные позиции майнерам
    if (minersPositions.length > 0) {
      itemsWithNewXY = compareMinersPositions3(
        minersPositions,
        itemsWithNewXY,
        props.type
      );
    }
    return itemsWithNewXY;
  };

  useEffect(() => {
    setRackSections(
      fillRackSections(
        rack?.rack_items || props.rackTemplate?.rack_items
      )
    );
  }, [props.type]);

  useEffect(() => {
    if (props.rackTypeError) return;
    const t = setTimeout(() => setErr(undefined), 2000);
    return () => clearTimeout(t);
  }, [err]);

  const isActive = useCallback(
    (num: number) => rackSections[num]?.pos !== RACK_SECTION_TYPE3.NULL,
    [rackSections]
  );

  const onSelectSection = (num: number, position: [number, number]) => {
    const changedSection = {
      pos: RACK_SECTION_TYPE3.EMPTY,
      x: +position[0],
      y: +position[1],
    };

    switch (rackSections[num].pos) {
      case RACK_SECTION_TYPE3.EMPTY:
        changedSection.pos = RACK_SECTION_TYPE3.NULL;
        break;
      case RACK_SECTION_TYPE3.NULL:
        changedSection.pos = RACK_SECTION_TYPE3.EMPTY;
        break;
      case RACK_SECTION_TYPE3.MINER:
        setErr({ ownError: [errors.locations__edit_rack] });
        return;
    }

    setRackSections((prev) => [
      ...prev.slice(0, num),
      changedSection,
      ...prev.slice(num + 1),
    ]);
  };

  const makeTitle = useMemo((): string => {
    const filledSections = rackSections.filter(
      (item) => item.pos !== RACK_SECTION_TYPE3.NULL
    );
    if (filledSections.length === 0) {
      return "Choose miners sections";
    }

    const height = props.type[1];
    const width = props.type[0];

    return `${height}x${width} (${filledSections.length.toString()} sections)`;
  }, [rackSections]);

  const onEditRack = async (width: number, height: number) => {
    if (!rack) return;
    const rec = await props.rackStore.editRack(rackSections, width, height);
    if (!rec) return;
    if (props.update) props.update(rec.rack_items, [width, height]);
    onSuccess(ok.success);
    props.onClose();
  };

  const onCreateRackTemplate = async (width: number, height: number) => {
    const r = await rackTemplatesStore.createRackTemplate(rackSections, width, height);
    if (!r) return;
    onSuccess(ok.success);
    props.onClose();
  };
  
  const onEditRackTemplate = async (width: number, height: number) => {
    if (!props.rackTemplate) return;
    const r = await rackTemplatesStore.editRackTemplate(props.rackTemplate.id, rackSections, width, height);
    if (!r) return;
    onSuccess(ok.success);
    props.onClose();
  };

  const onCreateOrEdit = () => {
    const height = props.type[1];
    const width = props.type[0];
    const filledSections = rackSections.filter(
      (item) => item.pos !== RACK_SECTION_TYPE3.NULL
    );

    if (filledSections.length === 0) {
      onError(errors.locations_select_rack_section);
      return;
    }

    if (!props.rackTemplate && !props.rackStore) {
      onCreateRackTemplate(width, height);
      return;
    }

    if (!!props.rackTemplate) {
      onEditRackTemplate(width, height);
      return;
    }
    onEditRack(width, height);
  };

  return {
    isEdit: isEditRack,
    title: makeTitle,
    rackSections,
    err,
    loading: rackTemplatesStore.isLoading || props.rackStore?.isLoading,

    onSelectSection,
    isActive,
    onCreateOrEdit,
  };
};


const RackEdit3 = observer((props: IRackEdit3Props) => {
  const { type } = props;
  const { formatMessage } = useIntl();
  const control = useControl(props);

  return (
    <div className={styles.wrapper}>
      <h5>{control.title}</h5>

      <div
        className={styles.rack}
        style={{
          gridTemplateColumns: makeGrid(type[0], `${2 * RACK_UNIT}px`),
          gridTemplateRows: makeGrid(type[1], "52px"),
          justifyContent: type[0] > 10 ? undefined : "center",
        }}
      >
        {control.rackSections.map((item, i) => {
          const currentIndex = props.reversed
            ? control.rackSections.length - 1 - i
            : i;
          const [X, Y] = xyPositionRackItem(currentIndex, props.type);

          return (
            <NumberOfColumn
              rack_type={type}
              index={i}
              reversed={props.reversed}
              key={i}
            >
              <MinerSection3
                item={item}
                position={[X, Y]}
                isActive={control.isActive(i)}
                onClick={control.onSelectSection.bind({}, i, [X, Y])}
              />
            </NumberOfColumn>
          );
        })}
      </div>

      {control.err && <Error errors={control.err} />}
      <Button
        className={styles.btn}
        onClick={control.onCreateOrEdit}
        disabled={!!control.err}
        loading={control.loading}
        type="primary"
      >
        {control.isEdit
          ? formatMessage({ id: "modal.equipment.btn_save" })
          : formatMessage({ id: "modal.create_object.btn" })}
      </Button>
    </div>
  );
});

RackEdit3.displayName = "RackEdit3";

export default WithErrorBoundry(RackEdit3);
